/** Small UI hooks: media queries, layout breakpoints, focus trapping, ticking clocks. */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type RefObject } from 'react';

function subscribeQuery(query: string) {
  return (cb: () => void) => {
    if (typeof window === 'undefined' || !window.matchMedia) return () => {};
    const mql = window.matchMedia(query);
    // Safari 13 only has addListener; modern browsers have addEventListener.
    if (mql.addEventListener) mql.addEventListener('change', cb);
    else mql.addListener(cb);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', cb);
      else mql.removeListener(cb);
    };
  };
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((cb: () => void) => subscribeQuery(query)(cb), [query]);
  return useSyncExternalStore(
    subscribe,
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  );
}

/** Wide layouts (iPad landscape, Chromebook, desktop) get side navigation and side sheets. */
export const WIDE_QUERY = '(min-width: 900px) and (orientation: landscape), (min-width: 1100px)';

export type Breakpoint = 'phone' | 'tablet' | 'desktop';

export function useBreakpoint(): Breakpoint {
  const tablet = useMediaQuery('(min-width: 600px)');
  const desktop = useMediaQuery('(min-width: 1100px)');
  return desktop ? 'desktop' : tablet ? 'tablet' : 'phone';
}

export function useWide(): boolean {
  return useMediaQuery(WIDE_QUERY);
}

export function useReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * Keeps keyboard focus inside `ref` while active, focuses the first control (or `initial`), closes on
 * Escape, and returns focus to the element that had it before.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean, onEscape?: () => void, initial?: RefObject<HTMLElement | null>) {
  const escRef = useRef(onEscape);
  escRef.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = initial?.current ?? node.querySelector<HTMLElement>('[data-autofocus]') ?? node.querySelector<HTMLElement>(FOCUSABLE) ?? node;
    // Wait a frame so enter animations do not fight the focus scroll.
    const raf = requestAnimationFrame(() => first.focus({ preventScroll: true }));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && escRef.current) {
        e.stopPropagation();
        escRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const firstEl = items[0] as HTMLElement;
      const lastEl = items[items.length - 1] as HTMLElement;
      if (e.shiftKey && (document.activeElement === firstEl || !node.contains(document.activeElement))) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey, true);
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    };
  }, [active, ref, initial]);
}

/**
 * Marks the app root inert (and hidden from screen readers) while a modal layer is open, so focus and
 * reading stay inside the layer. Layers are counted so nested dialogs work.
 */
let inertCount = 0;
export function useInertRoot(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const root = document.getElementById('root');
    inertCount += 1;
    if (root) {
      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('inert', '');
    }
    return () => {
      inertCount -= 1;
      if (inertCount <= 0 && root) {
        inertCount = 0;
        root.removeAttribute('aria-hidden');
        root.removeAttribute('inert');
      }
    };
  }, [active]);
}

/** Re-renders every `ms` and returns Date.now(). Pauses while the tab is hidden. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (id === null) id = setInterval(() => setNow(Date.now()), ms);
      setNow(Date.now());
    };
    const stop = () => {
      if (id !== null) clearInterval(id);
      id = null;
    };
    const onVis = () => (document.visibilityState === 'visible' ? start() : stop());
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ms]);
  return now;
}

/** Locks page scroll (used under sheets and modals). */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}
